import type { WorkoutPlanWithSchedule, WorkoutSummary } from '../../../../types/dataModel';

export interface WorkoutSlotAssignment {
  workoutId: string;
  planWorkoutId?: string | null;
  orderInDay: number;
  workout?: WorkoutSummary;
}

export interface PlanDay {
  workouts: WorkoutSlotAssignment[];
}

export function emptyDays(count: number): PlanDay[] {
  return Array.from({ length: count }, () => ({ workouts: [] }));
}

export function compactDaySlots(day: PlanDay): PlanDay {
  return {
    workouts: day.workouts.map((slot, index) => ({
      ...slot,
      orderInDay: index + 1,
    })),
  };
}

export function compactAllDays(days: PlanDay[]): PlanDay[] {
  return days.map(compactDaySlots);
}

export function daysFromPlan(plan: WorkoutPlanWithSchedule): PlanDay[] {
  const total = Math.max(1, plan.totalNoOfDays || 7);
  const days = emptyDays(total);
  for (const pw of plan.planWorkouts ?? []) {
    const idx = pw.dayNo - 1;
    if (idx >= 0 && idx < days.length) {
      days[idx].workouts.push({
        workoutId: pw.workoutId,
        planWorkoutId: pw.id,
        orderInDay: pw.orderInDay ?? 1,
        workout: pw.workout as WorkoutSummary | undefined,
      });
    }
  }
  for (const day of days) {
    day.workouts.sort((a, b) => a.orderInDay - b.orderInDay);
  }
  return days;
}

export function cloneDays(days: PlanDay[]): PlanDay[] {
  return days.map((day) => ({
    workouts: day.workouts.map((slot) => ({
      ...slot,
      workout: slot.workout ? { ...slot.workout } : undefined,
    })),
  }));
}

function slotsEqual(a: WorkoutSlotAssignment[], b: WorkoutSlotAssignment[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (slot, index) =>
      slot.workoutId === b[index].workoutId &&
      slot.orderInDay === b[index].orderInDay &&
      (slot.planWorkoutId ?? null) === (b[index].planWorkoutId ?? null),
  );
}

export function daysEqual(a: PlanDay[], b: PlanDay[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((day, index) => slotsEqual(day.workouts, b[index].workouts));
}

export function dayHasWorkouts(day: PlanDay | undefined): boolean {
  return (day?.workouts.length ?? 0) > 0;
}

export function countProgrammedDays(days: PlanDay[]): number {
  return days.filter(dayHasWorkouts).length;
}

export function countWorkoutSlots(days: PlanDay[]): number {
  return days.reduce((sum, day) => sum + day.workouts.length, 0);
}

export function sumEstimatedMinutes(days: PlanDay[]): number {
  return days.reduce(
    (sum, day) =>
      sum +
      day.workouts.reduce((slotSum, slot) => slotSum + (slot.workout?.estimatedDurationMinutes ?? 0), 0),
    0,
  );
}

export interface FlatPlanSlot {
  dayIndex: number;
  dayNo: number;
  orderInDay: number;
  workoutId: string;
  planWorkoutId?: string | null;
}

export function flattenSlots(days: PlanDay[]): FlatPlanSlot[] {
  const rows: FlatPlanSlot[] = [];
  days.forEach((day, dayIndex) => {
    day.workouts.forEach((slot) => {
      rows.push({
        dayIndex,
        dayNo: dayIndex + 1,
        orderInDay: slot.orderInDay,
        workoutId: slot.workoutId,
        planWorkoutId: slot.planWorkoutId ?? null,
      });
    });
  });
  return rows;
}
